import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, UserRound, Check, Ban, Sparkles } from 'lucide-react';
import { HEALTH_DETAIL_FIELDS } from '../../redux/healthRecordSlice';
import { entryDay, formatDay, recordedByLine, detailLines, subtypeLabel } from './healthFormat';

// ONE CARD OF THE TIMELINE — the same card on the patient's tab and inside an
// appointment, so a doctor and a patient are looking at the identical row.
//
// THE BADGES ARE THE POINT OF THE WHOLE RECORD. A line on somebody's medical
// history is worth what its provenance is worth, so every card says where it
// came from before it says anything else:
//   Verified          a doctor wrote it inside an appointment, or a doctor has
//                     since confirmed the patient's line
//   Self-reported     the patient (or their family) said so, nobody checked
//   Marked incorrect  retracted, with a reason. NOTHING IS EVER DELETED here,
//                     so the row stays and goes grey: a record that can be
//                     made to disappear is not a record.
//   New               (inside a visit only) added since this doctor last saw
//                     the patient — what they should read first.
//
// A doctor's ONLY action on a line is the optional "Verify" tick on a
// self-reported one. Flag was retired by the owner in round 9 (24 Sep 2026);
// the server refuses it (410 RETIRED) and no longer sends flagged* keys, so
// the card renders no flag state at all.

const badgeBase = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold';

function HealthEntryCard({ entry, canAct = false, busy = false, onVerify }) {
    const { t } = useTranslation();
    if (!entry) return null;

    const retracted = entry.status === 'incorrect';
    const verified = Boolean(entry.verified) || entry.recordedBy === 'doctor';
    const selfReported = entry.recordedBy === 'patient' && !entry.verified;
    const day = entryDay(entry);
    const endDay = formatDay(entry.endedAt);
    const details = detailLines(entry, HEALTH_DETAIL_FIELDS[entry.category], t);
    const subtype = subtypeLabel(entry.subtype, t);
    // Verify exists only for a line nobody has stood behind yet — verifying a
    // doctor's own entry would mean nothing — and never on a retracted one.
    const canVerify = canAct && selfReported && !retracted;

    return (
        <li
            data-testid={`health-entry-${entry.entryId}`}
            className={`relative rounded-2xl border p-4 shadow-sm transition-colors ${retracted
                ? 'border-gray-200 bg-gray-50 opacity-70'
                : 'border-gray-100 bg-white hover:border-[#4ABDBD]/40'}`}
        >
            {/* The timeline's teal spine, muted once the row is retracted. */}
            <span className={`absolute left-0 top-4 bottom-4 w-1 rounded-full ${retracted ? 'bg-gray-300' : 'bg-gradient-to-b from-[#4ABDBD] to-[#2D8A8A]'}`} />

            <div className="pl-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h4 data-testid="health-entry-title" className={`text-sm font-semibold ${retracted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                            {entry.title}
                        </h4>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {subtype && <span className="mr-2 rounded-md bg-[#4ABDBD]/10 px-1.5 py-0.5 font-medium text-[#2D8A8A]">{subtype}</span>}
                            {day && <span data-testid="health-entry-date">{day}</span>}
                            {/* "since"/"until" is what an end date MEANS on a
                                condition or a medicine — a second bare date
                                beside the first reads as two events. */}
                            {day && endDay && <span className="ml-2">{t('healthRecord.until', { date: endDay })}</span>}
                            {!day && endDay && <span>{t('healthRecord.since', { date: endDay })}</span>}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                        {entry.isNew === true && !retracted && (
                            <span data-testid="health-badge-new" className={`${badgeBase} bg-[#4ABDBD] text-white`}>
                                <Sparkles className="h-3 w-3" aria-hidden="true" />
                                {t('healthRecord.badge.new', 'New')}
                            </span>
                        )}
                        {retracted && (
                            <span data-testid="health-badge-incorrect" className={`${badgeBase} bg-gray-200 text-gray-600`}>
                                <Ban className="h-3 w-3" aria-hidden="true" />
                                {t('healthRecord.badge.incorrect')}
                            </span>
                        )}
                        {verified && (
                            <span data-testid="health-badge-verified" className={`${badgeBase} bg-[#4ABDBD]/15 text-[#2D8A8A]`}>
                                <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                                {t('healthRecord.badge.verified')}
                            </span>
                        )}
                        {selfReported && (
                            <span data-testid="health-badge-self-reported" className={`${badgeBase} bg-[#C7D6D5]/40 text-[#4b5f5e]`}>
                                <UserRound className="h-3 w-3" aria-hidden="true" />
                                {t('healthRecord.badge.self_reported')}
                            </span>
                        )}
                    </div>
                </div>

                {details.length > 0 && (
                    <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                        {details.map((d) => (
                            <div key={d.key} className="flex gap-1 text-xs">
                                <dt className="font-medium text-gray-500">{d.label}:</dt>
                                <dd className="text-gray-700">{d.value}</dd>
                            </div>
                        ))}
                    </dl>
                )}

                {retracted && entry.incorrectReason && (
                    <p data-testid="health-entry-incorrect-reason" className="mt-2 rounded-xl border border-gray-200 bg-white p-2 text-xs text-gray-500">
                        {t('healthRecord.incorrect_reason', { reason: entry.incorrectReason })}
                    </p>
                )}

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <p data-testid="health-entry-recorded-by" className="text-[11px] text-gray-400">
                        {recordedByLine(entry, t)}
                    </p>

                    {canVerify && (
                        <button
                            type="button"
                            data-testid={`health-entry-verify-${entry.entryId}`}
                            disabled={busy}
                            onClick={() => onVerify?.(entry)}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#2D8A8A] transition-colors hover:bg-[#4ABDBD]/10 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                        >
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('healthRecord.actions.verify', 'Verify')}
                        </button>
                    )}
                </div>
            </div>
        </li>
    );
}

export default HealthEntryCard;
